import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { toast } from "sonner";
import { Plus, X, AlertTriangle, Building2, Send } from "lucide-react";

function PlatformSender() {
  const [s, setS] = useState(null);
  const [dom, setDom] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const load = () => api.get("/admin/platform-sender").then((r) => { setS(r.data); setDom(r.data.sender_domain || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const checkDns = async () => {
    setChecking(true);
    try { const { data } = await api.post("/admin/platform-sender/dns-check"); setS((p) => ({ ...p, dns_check: data })); const st = data.checklist_status; const ok = Object.values(st).filter((v) => v === "ok").length; const fail = Object.values(st).filter((v) => v === "fail").length; (fail ? toast.warning : toast.success)(`DNS checked for ${data.domain}: ${ok} passed, ${fail} missing`); }
    catch (e) { toast.error(e.response?.data?.detail || "DNS check failed"); }
    finally { setChecking(false); }
  };
  const dns = s?.dns_check;
  const dnsDetail = dns ? {
    spf: dns.spf.ok ? dns.spf.records[0] : dns.spf.note,
    dkim: dns.dkim.ok ? `found selector(s): ${dns.dkim.selectors.join(", ")}` : dns.dkim.note,
    dmarc: dns.dmarc.ok ? `${dns.dmarc.records[0]}${dns.dmarc.policy === "none" ? " (p=none — tighten to quarantine later)" : ""}` : dns.dmarc.note,
    return_path: dns.return_path.ok ? "bounce CNAME found" : "no bounce./bounces. CNAME (optional if provider doesn't offer it)",
    mailbox: dns.mx.ok ? `MX: ${dns.mx.records[0]}` : "no MX record — replies/bounces will be lost",
  } : {};
  const Tick = ({ st }) => st === "ok" ? <span className="text-[#2F7A3E] font-mono text-[10px]">✓</span> : st === "fail" ? <span className="text-[#B23A2E] font-mono text-[10px]">✗</span> : <span className="text-[#1C2530]/40 font-mono text-[10px]">manual</span>;
  const save = async (patch) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/platform-sender", patch); setS(data); setDom(data.sender_domain || ""); toast.success(patch.force_managed !== undefined ? (patch.force_managed ? "All tenant system mail now routes via the platform sender" : "Tenants send from their own connected mailboxes again") : "Saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="platform-sender">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50"><Send className="w-3.5 h-3.5" /> Platform sender</div>
          <div className="text-sm font-medium text-[#1C2530] mt-1">Route all tenant system email through the platform domain</div>
          <div className="text-xs text-[#1C2530]/50 mt-0.5">Off = each tenant sends digests, nudges and password links from its own connected mailbox. On = everything goes out via the managed sender ({s.from_name}) on the platform domain — flip only after the DNS checklist is green.</div>
        </div>
        <button onClick={() => save({ force_managed: !s.force_managed })} disabled={busy || s.env_forced} role="switch" aria-checked={s.force_managed} data-testid="platform-sender-toggle"
          className={`shrink-0 font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border ${s.force_managed ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530] hover:bg-[#EFECE5]"} disabled:opacity-40`}>
          {s.force_managed ? "ON · platform sender" : "OFF · tenant mailboxes"}
        </button>
      </div>
      {s.env_forced && <div className="mt-2 font-mono text-[10px] text-[#E2711D]" data-testid="platform-sender-env">Forced ON by SYSTEM_EMAIL_FORCE_MANAGED env — the switch is read-only until that variable is removed.</div>}
      <div className="mt-4 flex gap-2 items-center">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50">Sender domain</span>
        <input value={dom} onChange={(e) => setDom(e.target.value)} placeholder="kelvex.io" data-testid="platform-sender-domain" className="border border-[#DFDAD0] px-3 py-1.5 text-sm w-56" />
        <button onClick={() => save({ sender_domain: dom })} disabled={busy} data-testid="platform-sender-domain-save" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5] disabled:opacity-40">Save</button>
        <button onClick={checkDns} disabled={checking || !s.sender_domain} data-testid="platform-sender-dns-check" className="border border-[#1C2530] bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">{checking ? "Checking…" : "Check DNS now"}</button>
        {dns && <span className="text-[11px] text-[#1C2530]/40" data-testid="platform-sender-dns-checked">DNS checked {new Date(dns.checked_at).toLocaleString()}</span>}
        {s.updated_at && <span className="text-[11px] text-[#1C2530]/40">updated {new Date(s.updated_at).toLocaleString()} by {s.updated_by}</span>}
      </div>
      <details className="mt-4" open={!!dns} data-testid="platform-sender-checklist">
        <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 hover:text-[#1C2530]">DNS &amp; provider checklist ({s.checklist.length} steps) — do these before switching ON</summary>
        <ol className="mt-2 space-y-1.5 pl-5 list-decimal text-sm text-[#1C2530]/80">
          {s.checklist.map((c) => {
            const st = dns?.checklist_status?.[c.key];
            return (
              <li key={c.key} data-testid={`checklist-${c.key}`} data-status={st || "unchecked"}>
                <span className="inline-flex items-center gap-2">{dns && <Tick st={st} />}<span>{c.label.replaceAll("kelvex.io", dom || "kelvex.io")}</span></span>
                {dns && dnsDetail[c.key] && <div className="font-mono text-[10px] text-[#1C2530]/50 mt-0.5 break-all" data-testid={`checklist-${c.key}-detail`}>{dnsDetail[c.key]}</div>}
              </li>
            );
          })}
        </ol>
      </details>
      <M365FixGuide />
    </div>
  );
}

function M365FixGuide() {
  const [domain, setDomain] = useState("visolutionz.com");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/platform-sender/dns-check?domain=${encodeURIComponent(domain.trim())}`); setRes(data); toast.success(`Checked ${data.domain}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setBusy(false); }
  };
  const d = domain.trim() || "yourdomain.com";
  const dash = d.replace(/\./g, "-");
  const initial = (d.split(".")[0] || "tenant");
  const St = ({ ok }) => res ? (ok ? <span className="text-[#2F7A3E] font-mono text-[10px] mr-2">✓ done</span> : <span className="text-[#B23A2E] font-mono text-[10px] mr-2">✗ missing</span>) : null;
  const steps = [
    { k: "dkim_enable", chk: res?.dkim.ok, t: <>Enable DKIM in Microsoft 365: <b>security.microsoft.com → Email &amp; collaboration → Policies &amp; rules → Threat policies → Email authentication settings → DKIM</b> → select <code>{d}</code> → Create DKIM keys.</> },
    { k: "dkim_cname", chk: res?.dkim.ok, t: <>Add the two CNAMEs it shows at your DNS host (usually): <code>selector1._domainkey.{d} → selector1-{dash}._domainkey.{initial}.onmicrosoft.com</code> and <code>selector2._domainkey.{d} → selector2-{dash}._domainkey.{initial}.onmicrosoft.com</code> (copy the exact values from the M365 page — the onmicrosoft prefix is your tenant's initial domain).</> },
    { k: "dkim_on", chk: res?.dkim.ok, t: <>Back in M365, switch <b>Sign messages for this domain with DKIM signatures</b> to On (may need up to an hour after DNS propagates).</> },
    { k: "dmarc", chk: res?.dmarc.ok, t: <>Add DMARC TXT at <code>_dmarc.{d}</code> with value <code>v=DMARC1; p=none; rua=mailto:dmarc@{d}; fo=1</code> — monitoring only, nothing gets rejected yet.</> },
    { k: "dmarc_mailbox", chk: res?.mx.ok, t: <>Make sure <code>dmarc@{d}</code> exists (shared mailbox or alias) so the aggregate reports land somewhere.</> },
    { k: "spf_keep", chk: res?.spf.ok, t: <>Keep SPF as one record: <code>v=spf1 include:spf.protection.outlook.com -all</code>. If the platform sender also sends for this domain later, add its include to the same record — never a second SPF record.</> },
    { k: "tighten", chk: res?.dmarc.policy && res.dmarc.policy !== "none", t: <>After 2–4 weeks of clean reports, tighten DMARC to <code>p=quarantine</code> then <code>p=reject</code>.</> },
  ];
  return (
    <details className="mt-4 border-t border-[#DFDAD0] pt-4" data-testid="m365-fix-guide">
      <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 hover:text-[#1C2530]">Microsoft 365 fix guide — enable DKIM &amp; add DMARC for a tenant domain</summary>
      <div className="mt-3 flex gap-2 items-center">
        <input value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="m365-domain" className="border border-[#DFDAD0] px-3 py-1.5 text-sm w-56" placeholder="visolutionz.com" />
        <button onClick={run} disabled={busy} data-testid="m365-check" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5] disabled:opacity-40">{busy ? "Checking…" : "Check this domain"}</button>
        {res && <span className="font-mono text-[10px] text-[#1C2530]/50" data-testid="m365-result">SPF {res.spf.ok ? "✓" : "✗"} · DKIM {res.dkim.ok ? "✓" : "✗"} · DMARC {res.dmarc.ok ? `✓ p=${res.dmarc.policy}` : "✗"}</span>}
      </div>
      <ol className="mt-3 space-y-2 pl-5 list-decimal text-sm text-[#1C2530]/80">
        {steps.map((s) => <li key={s.k} data-testid={`m365-step-${s.k}`} data-status={res ? (s.chk ? "ok" : "fail") : "unchecked"}><St ok={!!s.chk} />{s.t}</li>)}
      </ol>
    </details>
  );
}

const ALERT_LABEL = {
  seat_limit_reached: "Seat limit reached",
  seat_limit_approaching: "Nearing seat limit",
  trial_expired: "Trial expired",
  trial_expiring: "Trial expiring soon",
  usage_cap_reached: "Usage cap reached",
  usage_cap_approaching: "Nearing usage cap",
};

const STATUS_COLOR = {
  trial: "text-[#E2711D]", active: "text-[#2F7A3E]", suspended: "text-[#B23A2E]",
};

function ModuleToggles({ catalog, selected, onChange, idPrefix }) {
  const toggle = (k) => {
    if (k === "core_crm") return;
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  };
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {catalog.map((m) => {
        const on = m.core || selected.includes(m.key);
        return (
          <button key={m.key} type="button" data-testid={`${idPrefix}-mod-${m.key}`} onClick={() => toggle(m.key)}
            disabled={m.core}
            className={`flex items-center justify-between border px-3 py-1.5 text-xs rounded-sm transition-colors ${
              on ? "border-[#1C2530] bg-[#1C2530]/5 text-[#1C2530]" : "border-[#DFDAD0] text-[#1C2530]/50"} ${m.core ? "opacity-70" : "hover:bg-[#EFECE5]"}`}>
            <span>{m.label}</span>
            <span className="font-mono text-[9px] uppercase">{m.core ? "core" : on ? "on" : "off"}</span>
          </button>
        );
      })}
    </div>
  );
}

function ProvisionModal({ catalog, onClose, onDone }) {
  const [f, setF] = useState({ name: "", display_name: "", admin_email: "", admin_name: "",
    trial_days: 14, seat_limit: 5, usage_cap: 1000, sending_domain: "" });
  const [mods, setMods] = useState(catalog.map((m) => m.key));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [domTouched, setDomTouched] = useState(false);
  const set = (k, v) => setF((s) => {
    const next = { ...s, [k]: v };
    if (k === "admin_email" && !domTouched) next.sending_domain = (v.split("@")[1] || "").trim().toLowerCase();
    return next;
  });
  const submit = async () => {
    if (!f.name.trim() || !f.admin_email.trim() || !f.admin_name.trim()) { toast.error("Name, admin email and admin name are required"); return; }
    if (!/^(?:[a-z0-9-]+\.)+[a-z]{2,}$/.test((f.sending_domain || "").trim().toLowerCase())) { toast.error("Enter a valid sending domain, e.g. kelvex.io"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tenants", { ...f, trial_days: Number(f.trial_days),
        seat_limit: Number(f.seat_limit), usage_cap: Number(f.usage_cap), enabled_modules: mods });
      setResult(data); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to provision"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="provision-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto border border-[#DFDAD0]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#DFDAD0]">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#1C2530]">Provision new tenant</div>
          <button onClick={onClose} data-testid="provision-close"><X className="w-4 h-4" /></button>
        </div>
        {result ? (
          <div className="p-5 space-y-3" data-testid="provision-result">
            <div className="text-sm text-[#2F7A3E]">Tenant provisioned. Share these one-time credentials with the tenant Admin:</div>
            <div className="border border-[#DFDAD0] bg-[#F5F3EE] p-3 text-sm font-mono break-all">
              <div>Admin email: {result.admin_email}</div>
              <div data-testid="provision-result-domain">Sending domain: {result.sending_domain}</div>
              <div data-testid="provision-password">Temp password: {result.generated_password}</div>
              <div className="text-[#E2711D] mt-1">Status: trial · Billing: manual action required</div>
            </div>
            <div className="text-xs text-[#1C2530]/50">The admin can sign in once their workspace is activated in the data-isolation phase.</div>
            <button onClick={onClose} className="bg-[#1C2530] text-white px-4 py-2 text-sm">Done</button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {[["name", "Company / tenant name"], ["display_name", "Display name (optional)"],
              ["admin_name", "Tenant admin name"], ["admin_email", "Tenant admin email"]].map(([k, ph]) => (
              <input key={k} data-testid={`provision-${k}`} placeholder={ph} value={f[k]}
                onChange={(e) => set(k, e.target.value)} className="w-full border border-[#DFDAD0] p-2 text-sm" />
            ))}
            <div>
              <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Sending domain · email domain for this tenant's users</div>
              <input data-testid="provision-sending_domain" placeholder="e.g. kelvex.io" value={f.sending_domain}
                onChange={(e) => { setDomTouched(true); set("sending_domain", e.target.value); }}
                className="w-full border border-[#DFDAD0] p-2 text-sm" />
              <div className="text-[10px] text-[#1C2530]/45 mt-1">Auto-filled from the admin email — edit if the company uses a different domain. Only emails on this domain can be added as users.</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[["trial_days", "Trial days"], ["seat_limit", "Seat limit"], ["usage_cap", "Usage cap"]].map(([k, ph]) => (
                <div key={k}>
                  <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">{ph}</div>
                  <input type="number" data-testid={`provision-${k}`} value={f[k]}
                    onChange={(e) => set(k, e.target.value)} className="w-full border border-[#DFDAD0] p-2 text-sm" />
                </div>
              ))}
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Enabled modules</div>
              <ModuleToggles catalog={catalog} selected={mods} onChange={setMods} idPrefix="provision" />
            </div>
            <button data-testid="provision-submit" onClick={submit} disabled={busy}
              className="bg-[#1C2530] text-white px-4 py-2 text-sm w-full disabled:opacity-50">
              {busy ? "Provisioning…" : "Provision tenant"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EditModal({ tenant, catalog, onClose, onDone }) {
  const [f, setF] = useState({ status: tenant.status, seat_limit: tenant.seat_limit || 5,
    usage_cap: tenant.usage_cap || 0, trial_days: "", pwa_enabled: !!tenant.pwa_enabled });
  const [mods, setMods] = useState(tenant.enabled_modules || catalog.map((m) => m.key));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true);
    const body = { status: f.status, seat_limit: Number(f.seat_limit), usage_cap: Number(f.usage_cap), enabled_modules: mods, pwa_enabled: !!f.pwa_enabled };
    if (f.trial_days !== "") body.trial_days = Number(f.trial_days);
    try { await api.patch(`/admin/tenants/${tenant.id}`, body); toast.success("Tenant updated"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="edit-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto border border-[#DFDAD0]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#DFDAD0]">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#1C2530]">{tenant.display_name}</div>
          <button onClick={onClose} data-testid="edit-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Status</div>
              <select data-testid="edit-status" value={f.status} onChange={(e) => set("status", e.target.value)}
                className="w-full border border-[#DFDAD0] p-2 text-sm bg-white">
                {["trial", "active", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Extend trial (days, blank = keep)</div>
              <input type="number" data-testid="edit-trial-days" value={f.trial_days}
                onChange={(e) => set("trial_days", e.target.value)} className="w-full border border-[#DFDAD0] p-2 text-sm" />
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Seat limit</div>
              <input type="number" data-testid="edit-seat-limit" value={f.seat_limit}
                onChange={(e) => set("seat_limit", e.target.value)} className="w-full border border-[#DFDAD0] p-2 text-sm" />
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Usage cap (0 = unlimited)</div>
              <input type="number" data-testid="edit-usage-cap" value={f.usage_cap}
                onChange={(e) => set("usage_cap", e.target.value)} className="w-full border border-[#DFDAD0] p-2 text-sm" />
            </div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase text-[#1C2530]/50 mb-1">Enabled modules</div>
            <ModuleToggles catalog={catalog} selected={mods} onChange={setMods} idPrefix="edit" />
          </div>
          <label className="flex items-start gap-3 border border-[#DFDAD0] p-3 cursor-pointer" data-testid="edit-pwa-row">
            <input type="checkbox" checked={f.pwa_enabled} onChange={(e) => set("pwa_enabled", e.target.checked)} data-testid="edit-pwa-enabled" className="mt-0.5" />
            <span>
              <span className="block text-sm font-medium text-[#1C2530]">Installable app (PWA)</span>
              <span className="block text-xs text-[#1C2530]/55">Lets this tenant's users add the CRM to their phone home screen and open it full-screen. Serves a branded manifest and icons from their logo/colours; API stays live (no offline data).</span>
            </span>
          </label>
          <button data-testid="edit-submit" onClick={submit} disabled={busy}
            className="bg-[#1C2530] text-white px-4 py-2 text-sm w-full disabled:opacity-50">
            {busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function InstalledCell({ tenantId }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get(`/admin/tenants/${tenantId}/installs`).then((r) => setS(r.data)).catch(() => setS({ count: 0, users: [] })); }, [tenantId]);
  if (!s) return <span className="text-xs text-[#1C2530]/40">…</span>;
  const ago = (iso) => { if (!iso) return ""; const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`; };
  return (
    <div className="relative">
      <button onClick={() => s.count && setOpen((v) => !v)} data-testid={`tenant-installs-${tenantId}`} className={`text-left ${s.count ? "hover:underline" : "cursor-default"}`}>
        <div className="text-sm text-[#1C2530]">{s.count} user{s.count === 1 ? "" : "s"}{s.push_users ? <span className="text-[#3E8F99]"> · {s.push_users} push</span> : null}</div>
        <div className="text-[11px] text-[#1C2530]/50">{s.count ? `last opened ${ago(s.last_opened_at)}` : "not installed yet"}</div>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 border border-[#DFDAD0] bg-white shadow-[3px_3px_0px_#DFDAD0] p-3 text-xs" data-testid={`tenant-installs-list-${tenantId}`}>
          {s.users.map((u) => (
            <div key={u.user_id} className="flex justify-between gap-2 py-1 border-b border-[#DFDAD0] last:border-0">
              <span className="truncate"><span className="text-[#1C2530]">{u.name}</span> <span className="text-[#1C2530]/50">{u.platform}</span></span>
              <span className="text-[#1C2530]/50 whitespace-nowrap">{ago(u.last_opened_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SuperAdmin() {
  const [tenants, setTenants] = useState(null);
  const [overview, setOverview] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [provision, setProvision] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => setTenants([]));
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/admin/modules").then((r) => setCatalog(r.data.modules)).catch(() => {});
  }, []);

  return (
    <div data-testid="super-admin-page">
      <PageHeader eyebrow="Platform Control Plane" title="Tenants"
        right={<button data-testid="provision-open" onClick={() => setProvision(true)}
          className="flex items-center gap-2 bg-[#E50914] text-white px-4 py-2 text-sm rounded-sm hover:bg-[#c40810]">
          <Plus className="w-4 h-4" /> Provision tenant</button>} />
      <div className="p-8 space-y-6">
        {overview && (
          <div className="grid grid-cols-4 gap-3" data-testid="admin-overview">
            {[["Tenants", overview.tenant_count], ["Trials", overview.trial_count],
              ["Active", overview.active_count], ["Suspended", overview.suspended_count]].map(([l, v]) => (
              <div key={l} className="border border-[#DFDAD0] bg-white p-4">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50">{l}</div>
                <div className="text-2xl font-semibold text-[#1C2530] mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        {overview?.alerts?.length > 0 && (
          <div className="border border-[#E2711D]/40 bg-[#E2711D]/10 p-4 space-y-1" data-testid="admin-alerts">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#8a4a12]">
              <AlertTriangle className="w-3.5 h-3.5" /> Attention needed
            </div>
            {overview.alerts.map((a, i) => (
              <div key={i} className="text-sm text-[#8a4a12]">
                <b>{a.tenant}</b> — {ALERT_LABEL[a.alert] || a.alert}
                {a.alert.startsWith("seat") ? ` (${a.seat_usage}/${a.seat_limit} seats)` : ""}
                {a.trial_days_left != null && a.alert.startsWith("trial") ? ` (${a.trial_days_left}d left)` : ""}
              </div>
            ))}
          </div>
        )}

        {!tenants ? <Loading /> : tenants.length === 0 ? <Empty label="NO_TENANTS" /> : (
          <div className="border border-[#DFDAD0] bg-white" data-testid="tenants-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#DFDAD0] text-left font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50">
                  <th className="px-4 py-2.5">Tenant</th><th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Trial ends</th><th className="px-4 py-2.5">Seats</th>
                  <th className="px-4 py-2.5">Usage</th><th className="px-4 py-2.5">Modules</th><th className="px-4 py-2.5">Installed</th><th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DFDAD0]">
                {tenants.map((t) => (
                  <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="hover:bg-[#F5F3EE]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1C2530]">{t.display_name}</div>
                      <div className="text-xs text-[#1C2530]/50">{t.admin_email}</div>
                    </td>
                    <td className={`px-4 py-3 font-mono text-[11px] uppercase ${STATUS_COLOR[t.status] || ""}`}>{t.status}</td>
                    <td className="px-4 py-3 text-xs">{t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">{t.seat_usage}/{t.seat_limit ?? "∞"}</td>
                    <td className="px-4 py-3">{t.usage_count}{t.usage_cap ? `/${t.usage_cap}` : ""}</td>
                    <td className="px-4 py-3">{t.enabled_modules ? t.enabled_modules.length : "all"}</td>
                    <td className="px-4 py-3">{t.pwa_enabled ? <InstalledCell tenantId={t.id} /> : <span className="text-[11px] text-[#1C2530]/40">PWA off</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`tenant-edit-${t.id}`} onClick={() => setEditing(t)}
                        className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530] hover:underline">Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {provision && <ProvisionModal catalog={catalog} onClose={() => setProvision(false)} onDone={load} />}
      {editing && <EditModal tenant={editing} catalog={catalog} onClose={() => setEditing(null)} onDone={load} />}
      <div className="mt-6"><PlatformSender /></div>
    </div>
  );
}
